/* ==========================================================================
   Coolant Mixture Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-cmc]');
  if (!root) return;

  /* Ethylene glycol freeze and boil points by volume percentage. Standard
     published figures. Boil points assume a 15 psi cap. */
  var TABLE = [
    { pct:  0, freeze:  32, boil: 212 },
    { pct: 10, freeze:  25, boil: 214 },
    { pct: 20, freeze:  16, boil: 216 },
    { pct: 30, freeze:   4, boil: 220 },
    { pct: 40, freeze: -12, boil: 222 },
    { pct: 50, freeze: -34, boil: 226 },
    { pct: 60, freeze: -62, boil: 230 },
    { pct: 70, freeze: -84, boil: 235 }
  ];

  /* Past about 70% glycol the freeze point starts rising again and heat
     transfer gets noticeably worse. */
  var MAX_USEFUL_PCT = 70;
  var MIN_INHIBITOR_PCT = 33;
  var CAP_BOIL_BONUS = 45;   /* a 15 psi cap raises the boiling point by roughly this much */

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function interp(pct, key) {
    if (pct <= TABLE[0].pct) return TABLE[0][key];
    for (var i = 1; i < TABLE.length; i++) {
      if (pct <= TABLE[i].pct) {
        var a = TABLE[i - 1], b = TABLE[i];
        var f = (pct - a.pct) / (b.pct - a.pct);
        return a[key] + (b[key] - a[key]) * f;
      }
    }
    return TABLE[TABLE.length - 1][key];
  }

  function compute(input) {
    if (!isFinite(input.capacity) || input.capacity <= 0) {
      return { ok: false, errors: ['Enter the system capacity in quarts.'] };
    }
    if (!isFinite(input.target) || input.target <= 0 || input.target > 100) {
      return { ok: false, errors: ['Enter a target concentration between 20 and 70 percent.'] };
    }
    var current = isFinite(input.current) ? input.current : 0;
    if (current < 0 || current > 100) {
      return { ok: false, errors: ['Current concentration has to be between 0 and 100 percent.'] };
    }

    var C = input.capacity, t = input.target / 100, c = current / 100;
    var freeze = interp(input.target, 'freeze');
    var boil   = interp(input.target, 'boil');

    var status, figures = [], rows = [];

    if (input.target < MIN_INHIBITOR_PCT) {
      status = { key: 'now', label: num(freeze, 0) + 'F freeze point',
                 note: 'Below 33% there is not enough inhibitor package to protect the water pump, heater core and aluminium.' };
    } else if (input.target > MAX_USEFUL_PCT) {
      status = { key: 'soon', label: num(freeze, 0) + 'F freeze point',
                 note: 'Past 70% glycol the freeze point climbs again and the coolant carries heat noticeably worse.' };
    } else {
      status = { key: 'good', label: num(freeze, 0) + 'F freeze point',
                 note: 'Boils at ' + num(boil, 0) + 'F, or about ' + num(boil + CAP_BOIL_BONUS, 0) + 'F with a 15 psi cap holding pressure.' };
    }

    if (current === 0) {
      /* Dry fill. */
      var conc = C * t;
      var water = C - conc;
      if (input.product === 'premix') {
        figures.push({ label: 'Pre-diluted 50/50 needed', value: num(C, 1) + ' qt',
                       sub: 'A 50/50 premix fills the system on its own. Add no water.' });
        rows.push({ head: 'Dry fill with premix' });
        rows.push({ label: 'Premix', value: num(C, 1) + ' qt' });
        rows.push({ label: 'Water', value: 'None' });
        rows.push({ label: 'Resulting mix', value: '50%' });
      } else {
        figures.push({ label: 'Concentrate needed', value: num(conc, 2) + ' qt',
                       sub: 'Plus ' + num(water, 2) + ' qt of distilled water.' });
        figures.push({ label: 'Distilled water', value: num(water, 2) + ' qt',
                       sub: 'Use distilled. Tap water leaves scale and eats aluminium.' });
        rows.push({ head: 'Dry fill to ' + num(input.target, 0) + '%' });
        rows.push({ label: 'Concentrate', value: num(conc, 2) + ' qt' });
        rows.push({ label: 'Distilled water', value: num(water, 2) + ' qt' });
        rows.push({ label: 'Total', value: num(C, 1) + ' qt' });
      }
    } else if (Math.abs(current - input.target) < 0.5) {
      figures.push({ label: 'Nothing to do', value: 'Mix is correct',
                     sub: 'Already at ' + num(current, 0) + '%.' });
      rows.push({ head: 'No change needed' });
    } else if (current < input.target) {
      /* Drain some out, replace with pure concentrate. */
      var drain = C * (t - c) / (1 - c);
      figures.push({ label: 'Drain and replace', value: num(drain, 2) + ' qt',
                     sub: 'Remove ' + num(drain, 2) + ' qt of coolant, top back up with the same volume of full strength concentrate.' });
      rows.push({ head: 'Correcting ' + num(current, 0) + '% up to ' + num(input.target, 0) + '%' });
      rows.push({ label: 'Drain out', value: num(drain, 2) + ' qt' });
      rows.push({ label: 'Add concentrate', value: num(drain, 2) + ' qt' });
      rows.push({ label: 'System total', value: num(C, 1) + ' qt' });
    } else {
      /* Too strong. Drain and replace with water. */
      var drainW = C * (c - t) / c;
      figures.push({ label: 'Drain and replace', value: num(drainW, 2) + ' qt',
                     sub: 'Remove ' + num(drainW, 2) + ' qt of coolant, top back up with the same volume of distilled water.' });
      rows.push({ head: 'Correcting ' + num(current, 0) + '% down to ' + num(input.target, 0) + '%' });
      rows.push({ label: 'Drain out', value: num(drainW, 2) + ' qt' });
      rows.push({ label: 'Add distilled water', value: num(drainW, 2) + ' qt' });
      rows.push({ label: 'System total', value: num(C, 1) + ' qt' });
    }

    rows.push({ head: 'Protection at ' + num(input.target, 0) + '%' });
    rows.push({ label: 'Freeze point', value: num(freeze, 0) + ' F' });
    rows.push({ label: 'Boil point, no pressure', value: num(boil, 0) + ' F' });
    rows.push({ label: 'Boil point, 15 psi cap', value: num(boil + CAP_BOIL_BONUS, 0) + ' F' });

    var warnings = [];
    if (input.product === 'premix' && input.target !== 50) {
      warnings.push('Pre-diluted coolant is already 50/50. You cannot reach ' + num(input.target, 0) + '% with it. Buy concentrate for anything other than a 50% mix.');
    }

    return { ok: true, errors: [], warnings: warnings, status: status,
             figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "capacity",
          "key": "c",
          "type": "number"
      },
      {
          "name": "target",
          "key": "t",
          "type": "number"
      },
      {
          "name": "current",
          "key": "cu",
          "type": "number"
      },
      {
          "name": "product",
          "key": "pr",
          "type": "select"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-cmc__figure">'
        + '<div class="pc-cmc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-cmc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-cmc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-cmc__row pc-cmc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-cmc__row">'
        + '<span class="pc-cmc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-cmc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_CMC = { compute: compute };
})();
